<template>
  <div class="map-selector-entry">
    <Button class="map-selector-trigger" @click="openMapSelector" type="primary" icon="ios-map" size="small">
      选择地图
    </Button>

    <Modal
      v-model="mapSelectorVisible"
      width="980"
      :styles="{ top: '36px' }"
      footer-hide
      :closable="false"
      class-name="map-selector-modal"
    >
      <div class="realm-shell">
        <div class="realm-toolbar">
          <div v-if="currentRegion" class="realm-crumb">
            <button :class="{ active: !currentRegion }" @click="backToOverview">界域总览</button>
            <template v-if="currentRegion">
              <Icon type="ios-arrow-forward" />
              <span>{{ currentRegion.label }}</span>
            </template>
          </div>
          <Button class="realm-close" type="text" icon="ios-close" @click="closeMapSelector"></Button>
        </div>

        <main class="realm-content">
          <div v-if="!currentRegion" class="region-overview">
            <div class="overview-heading">
              <h2>修仙界域图</h2>
              <p>每个区域拥有独立标识，点击界域进入小地图，查看怪物与掉落。</p>
            </div>
            <div class="overview-path">大地图 · 界域总览</div>
            <div class="region-grid">
              <article
                v-for="region in worldRegions"
                :key="region.key"
                class="region-card"
                :class="{ recommended: isRecommendedRegion(region), current: isCurrentRegion(region), empty: !region.maps.length }"
                :style="{ '--region-color': region.color }"
                @click="enterRegion(region)"
              >
                <div class="region-title-row">
                  <h3>{{ region.label }}</h3>
                  <span class="region-level">{{ region.range }}</span>
                </div>
                <div class="region-type">{{ region.typeLabel }}</div>
                <p class="region-card-description" :title="region.description">{{ region.description }}</p>
                <div class="region-footer">
                  <span class="region-map-count">{{ region.maps.length }} 图</span>
                  <strong class="region-heat" :class="heatClass(region)">{{ heatText(region) }}</strong>
                  <span class="region-online"><i></i>在线 <b>{{ getRegionOnline(region) }}</b> 人</span>
                </div>
                <Spin v-if="region.key === 'dungeon' && dungeonLoading" size="small" class="region-loading"></Spin>
              </article>
            </div>

            <div v-if="!dungeonLoading && !worldRegions.length" class="realm-empty">
              <Icon type="ios-map-outline" />
              <strong>此区域暂无地图</strong>
              <span>可以刷新后再来查看。</span>
            </div>
          </div>

          <div v-else class="region-detail" :style="{ '--region-color': currentRegion.color }">
            <div class="region-summary">
              <span class="summary-icon"><Icon :type="currentRegion.icon" /></span>
              <div class="summary-copy">
                <div class="summary-title-line">
                  <h3>{{ currentRegion.label }}</h3>
                  <span>{{ currentRegion.typeLabel }}</span>
                </div>
                <p>{{ currentRegion.description }}</p>
              </div>
            </div>

            <div class="map-detail-layout">
              <div class="spot-list">
                <button
                  v-for="map in currentRegion.maps"
                  :key="map.mapId"
                  class="spot-card"
                  :class="{ selected: isSelectedMap(map), recommended: isRecommended(map) }"
                  @click="selectSubMap(map)"
                >
                  <span class="spot-copy">
                    <span class="spot-name">
                      <span>{{ map.mapName }}</span>
                      <i>Lv.{{ map.minLv || 1 }} - {{ map.maxLv || map.minLv || 1 }}</i>
                    </span>
                    <span class="spot-meta">
                      <span><i class="map-status-dot"></i>{{ getMapMonsters(map).length }} 种怪物</span>
                      <span><Icon type="ios-people" /> {{ map.onlineNum || 0 }} 人挂机</span>
                    </span>
                    <span class="spot-description" :title="map.describeTion || '暂无地图描述'">{{ map.describeTion || '暂无地图描述' }}</span>
                  </span>
                </button>

                <div v-if="!currentRegion.maps.length" class="realm-empty compact">
                  <Icon type="ios-compass-outline" />
                  <strong>当前没有可进入的地图</strong>
                </div>
              </div>

              <section v-if="selectedMap" class="map-intel">
                <div class="intel-heading">
                  <div>
                    <h3>{{ selectedMap.mapName }}</h3>
                    <span>{{ getDangerText(selectedMap) }} · 共 {{ getMapMonsters(selectedMap).length }} 种怪物</span>
                  </div>
                  <Button
                    type="primary"
                    icon="ios-play"
                    :loading="battleLoading"
                    @click="startBattle"
                  >{{ battleLoading ? '进入中...' : '开始战斗' }}</Button>
                </div>

                <div v-if="monsterLoading" class="monster-loading"><Spin></Spin><span>正在载入怪物情报...</span></div>
                <div v-else-if="selectedMonsters.length" class="monster-detail-list">
                  <article v-for="monster in selectedMonsters" :key="monster.monsterId" class="monster-detail-card">
                    <div class="monster-card-head">
                      <strong>{{ monster.name }}</strong>
                      <span>Lv.{{ monster.level || 0 }}</span>
                    </div>
                    <div class="monster-stats">
                      <span>气血 <b>{{ monster.life || 0 }}</b></span>
                      <span>攻击 <b>{{ formatMonsterAttack(monster) }}</b></span>
                      <span>力道防御 <b>{{ monster.defence || 0 }}</b></span>
                      <span>灵力防御 <b>{{ monster.magicDefense || 0 }}</b></span>
                      <span>速度 <b>{{ formatMonsterNumber(monster.speed) }}</b></span>
                      <span>暴击 <b>{{ formatMonsterPercent(monster.explode != null ? monster.explode : monster.critical) }}</b></span>
                      <span>爆伤 <b>{{ formatMonsterPercent(monster.criDamage) }}</b></span>
                    </div>
                  </article>
                </div>
                <div v-else class="loot-empty monster-empty">当前地图暂无怪物图鉴数据</div>

                <div class="unified-loot">
                  <div class="monster-loot-title">掉落</div>
                  <div v-if="selectedDrops.length" class="loot-list">
                    <span
                      v-for="(drop, index) in selectedDrops"
                      :key="index"
                      class="loot-item"
                      :style="{ '--loot-color': getDropColor(drop.color) }"
                    >
                      <i></i>{{ getDropDisplayName(drop.itemName) }}
                      <small>{{ getDropQualityText(drop.color) }}<template v-if="getDropRateText(drop)"> {{ getDropRateText(drop) }}</template></small>
                    </span>
                  </div>
                  <div v-else class="loot-empty">暂无掉落情报</div>
                </div>

                <div class="intel-actions">
                  <Button icon="ios-book-outline" @click="viewMapDrops(selectedMap)">查看完整掉落</Button>
                  <Button icon="ios-skull-outline" @click="viewMapMonsters(selectedMap)">打开怪物图鉴</Button>
                </div>
              </section>
            </div>
          </div>
        </main>

        <footer class="realm-footer">
          <span>当前角色等级 Lv.{{ userLevel }}</span>
          <Button size="small" icon="ios-refresh" :loading="dungeonLoading" @click="refreshSubMaps">刷新地图</Button>
        </footer>
      </div>
    </Modal>
  </div>
</template>

<script>
export default {
  name: 'MapSelector',
  props: {
    mapList: {
      type: Array,
      default: () => []
    },
    userActivity: {
      type: Object,
      default: () => ({ level: 1 })
    },
    mapLootsList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      mapSelectorVisible: false,
      selectedLevelRange: 'all',
      currentRegionKey: '',
      selectedMap: null,
      battleLoading: false,
      dungeonLoading: false,
      dungeonMaps: [],
      monsterLoading: false,
      monsterListLoading: false,
      monsterListLoaded: false,
      monsterCodexList: [],
      monsterDetailCache: {},
      stageDefinitions: [
        {
          key: 'luoxing-valley', label: '落星山谷', range: 'Lv.1 - 12', max: 12,
          typeLabel: '山谷', icon: 'ios-leaf', color: '#2e9e5b',
          description: '星辉洒落之地，灵气初凝，是低阶修士的引路之所。'
        },
        {
          key: 'hezhou-realm', label: '和洲域', range: 'Lv.13 - 20', max: 20,
          typeLabel: '大域', icon: 'ios-home', color: '#1e6fd9',
          description: '广袤平川，凡人国度与修仙小派错落交织，暗藏机缘。'
        },
        {
          key: 'haize-commandery', label: '海泽郡', range: 'Lv.21 - 30', max: 30,
          typeLabel: '海郡', icon: 'ios-boat', color: '#7c93ad',
          description: '千湖交错，水泽密布，传闻深处蛰伏着上古水妖。'
        },
        {
          key: 'guixu-sea', label: '归墟海', range: 'Lv.31 - 39', max: 39,
          typeLabel: '海域', icon: 'ios-water', color: '#5b9cff',
          description: '万水汇聚之渊，狂风巨浪间隐约可见沉没的仙家楼阁。'
        },
        {
          key: 'cloud-ruins', label: '云海遗址', range: 'Lv.40 - 50', max: 50,
          typeLabel: '遗址', icon: 'ios-planet', color: '#7a5cd6',
          description: '悬浮云端的破碎古城，残存的道则威压令人寸步难行。'
        },
        {
          key: 'jinling-city', label: '金陵城', range: 'Lv.51+', max: Infinity,
          typeLabel: '城池', icon: 'ios-home', color: '#c98a00',
          description: '龙脉汇聚的繁华仙都，万宗来朝，也是天下奇珍的汇聚之地。'
        }
      ]
    };
  },
  computed: {
    userLevel() {
      return Number(this.userActivity && this.userActivity.level) || 1;
    },
    regularMaps() {
      return (this.mapList || []).filter(map => map && !this.isSpecialMap(map));
    },
    activityMaps() {
      return (this.mapList || [])
        .filter(map => this.isActivityMap(map))
        .slice()
        .sort((a, b) => (Number(a.minLv) || 0) - (Number(b.minLv) || 0));
    },
    regularRegions() {
      const regions = this.stageDefinitions.map(stage => ({ ...stage, maps: [] }));
      this.regularMaps.forEach(map => {
        const minLv = Number(map.minLv) || 1;
        const index = this.stageDefinitions.findIndex(stage => minLv <= stage.max);
        if (index >= 0) regions[index].maps.push(map);
      });
      regions.forEach(region => region.maps.sort((a, b) => (Number(a.minLv) || 0) - (Number(b.minLv) || 0)));
      return regions;
    },
    worldRegions() {
      return this.regularRegions.concat([
        {
          key: 'activity', label: '限时活动', range: '限时开放', typeLabel: '活动区域',
          icon: 'ios-calendar', color: '#b45f3f',
          description: '节令、限时与特殊玩法地图汇聚于此，开放时间以活动公告为准。',
          maps: this.activityMaps
        },
        {
          key: 'dungeon', label: '组队秘境', range: '多人协作', typeLabel: '组队副本',
          icon: 'ios-people', color: '#5b4b8a',
          description: '召集同道共同挑战高强度秘境，副本地图与在线队伍信息集中于此。',
          maps: this.dungeonMaps
        }
      ]);
    },
    currentRegion() {
      if (!this.currentRegionKey) return null;
      return this.worldRegions.find(region => region.key === this.currentRegionKey) || null;
    },
    selectedDrops() {
      if (!this.selectedMap) return [];
      const source = this.getMapDrops(this.selectedMap);
      const seen = new Set();
      return this.getSortedDropItems(source && source.itemsVoList ? source.itemsVoList : [])
        .filter(drop => {
          const key = [this.getDropDisplayName(drop.itemName), Number(drop.color) || 0, Number(drop.level) || 0].join('|');
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 12);
    },
    selectedMonsters() {
      if (!this.selectedMap) return [];
      return this.getMapMonsters(this.selectedMap).map(monster => {
        return this.monsterDetailCache[String(monster.monsterId)] || monster;
      });
    }
  },
  methods: {
    openMapSelector() {
      this.mapSelectorVisible = true;
      this.selectedLevelRange = 'all';
      this.currentRegionKey = '';
      this.selectedMap = null;
      this.loadMonsterCodexList();
      this.loadDungeonMaps();
    },
    closeMapSelector() {
      this.mapSelectorVisible = false;
    },
    backToOverview() {
      this.currentRegionKey = '';
      this.selectedMap = null;
    },
    async changeMapCategory(category) {
      this.currentRegionKey = '';
      this.selectedMap = null;
      if (category === 'dungeon') await this.loadDungeonMaps();
    },
    enterRegion(region) {
      this.currentRegionKey = region.key;
      this.selectedMap = null;
    },
    findRecommendedMap(maps) {
      return (maps || []).find(map => this.isRecommended(map)) || null;
    },
    isRecommendedRegion(region) {
      return (region.maps || []).some(map => this.isRecommended(map));
    },
    isCurrentRegion(region) {
      const currentMapName = this.normalizeMapName(this.userActivity && this.userActivity.mapName);
      return !!currentMapName && (region.maps || []).some(map => this.normalizeMapName(map.mapName) === currentMapName);
    },
    isRecommended(map) {
      const min = Number(map && map.minLv) || 1;
      const max = Number(map && map.maxLv) || min;
      return this.userLevel >= min && this.userLevel <= max;
    },
    isSelectedMap(map) {
      return !!(this.selectedMap && String(this.selectedMap.mapId) === String(map.mapId));
    },
    async selectSubMap(map) {
      this.selectedMap = map;
      await this.loadMonsterDetails(map);
    },
    getRegionOnline(region) {
      return (region.maps || []).reduce((sum, map) => sum + (Number(map.onlineNum) || 0), 0);
    },
    getHeatLevel(region) {
      const count = this.getRegionOnline(region);
      if (count <= 0) return 0;
      if (count <= 5) return 1;
      if (count <= 15) return 2;
      if (count <= 30) return 3;
      if (count <= 60) return 4;
      return 5;
    },
    heatText(region) {
      const level = this.getHeatLevel(region);
      return '●'.repeat(level) + '○'.repeat(5 - level);
    },
    heatClass(region) {
      const level = this.getHeatLevel(region);
      if (level <= 1) return 'heat-low';
      if (level <= 3) return 'heat-mid';
      return 'heat-high';
    },
    isActivityMap(map) {
      return Number(map && map.mapType) === 5;
    },
    isSpecialMap(map) {
      return !!(map && map.isDungeonMap) || this.isActivityMap(map);
    },
    getMapIcon(map) {
      if (map && map.isDungeonMap) return 'ios-people';
      if (this.isActivityMap(map)) return 'ios-calendar';
      const name = String(map && (map.mapName || map.name) || '');
      if (/水|湖|河|海|渡|潮|溪|湾|岛/.test(name)) return 'ios-water';
      if (/山|谷|林|森|岭|原|野/.test(name)) return 'ios-leaf';
      if (/洞|府|宫|城|关|门|殿/.test(name)) return 'ios-home';
      if (/遗|墟|墓|塔|台|阵/.test(name)) return 'ios-planet';
      if (/火|炎|焰|赤/.test(name)) return 'ios-flame';
      return 'ios-navigate';
    },
    getMapKindText(map) {
      const labels = { 1: '普通', 2: '材料', 3: '挑战', 4: '金币', 5: '活动', 6: 'BOSS', 7: '真灵', 8: '修真' };
      return labels[Number(map && map.mapType)] || '历练';
    },
    getDangerText(map) {
      const maxLevel = Number(map && (map.maxLv || map.minLv)) || 1;
      if (maxLevel <= 10) return '外围 · 低危';
      if (maxLevel <= 20) return '中段 · 中危';
      if (maxLevel <= 30) return '深处 · 高危';
      return '核心 · 极危';
    },
    normalizeMapName(value) {
      return String(value || '').replace(/\s+/g, '').trim();
    },
    getMapMonsters(map) {
      const mapName = this.normalizeMapName(map && map.mapName);
      if (!mapName) return [];
      return (this.monsterCodexList || []).filter(monster => {
        const names = Array.isArray(monster && monster.mapNames) ? monster.mapNames : [];
        return names.some(name => this.normalizeMapName(name) === mapName);
      });
    },
    getEliteMonsterCount(map) {
      return this.getMapMonsters(map).filter(monster => {
        const detail = this.monsterDetailCache[String(monster.monsterId)] || monster;
        return !!detail.prefix || /精英|首领|王$|统领/.test(String(detail.name || ''));
      }).length;
    },
    async loadMonsterCodexList(forceRefresh = false) {
      if (this.monsterListLoading || (!forceRefresh && this.monsterListLoaded)) return;
      this.monsterListLoading = true;
      try {
        const res = await this.$http.get('/gameChara/monsterCodexList');
        this.monsterCodexList = (res && res.data && res.data.data) || [];
        this.monsterListLoaded = true;
      } catch (error) {
        this.monsterCodexList = [];
      } finally {
        this.monsterListLoading = false;
      }
    },
    async loadMonsterDetails(map) {
      if (!this.monsterListLoaded) await this.loadMonsterCodexList();
      const monsters = this.getMapMonsters(map);
      const pending = monsters.filter(monster => !this.monsterDetailCache[String(monster.monsterId)]);
      if (!pending.length) return;
      this.monsterLoading = true;
      try {
        await Promise.all(pending.map(async monster => {
          const res = await this.$http.get('/gameChara/monsterCodexDetail', {
            params: { monsterId: monster.monsterId }
          });
          const detail = (res && res.data && res.data.data) || monster;
          this.$set(this.monsterDetailCache, String(monster.monsterId), { ...monster, ...detail });
        }));
      } catch (error) {
        this.$Message.warning('部分怪物情报暂时无法加载');
      } finally {
        this.monsterLoading = false;
      }
    },
    formatMonsterAttack(monster) {
      const min = Number(monster && monster.attackMin);
      const max = Number(monster && monster.attackMax);
      if (Number.isFinite(min) && Number.isFinite(max)) return min === max ? String(min) : `${min}-${max}`;
      return Number.isFinite(max) ? String(max) : (Number.isFinite(min) ? String(min) : '0');
    },
    formatMonsterNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? String(number) : '0';
    },
    formatMonsterPercent(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '0%';
      const percent = Math.abs(number) <= 1 ? number * 100 : number;
      return `${Number(percent.toFixed(2))}%`;
    },
    getMonsterDrops(monster) {
      const ownDrops = monster && (monster.dropItems || monster.itemsVoList || monster.drops);
      if (Array.isArray(ownDrops) && ownDrops.length) return this.getSortedDropItems(ownDrops).slice(0, 8);
      const monsterId = String(monster && monster.monsterId || '');
      const monsterName = String(monster && monster.name || '');
      const matched = this.selectedDrops.filter(drop => {
        if (monsterId && drop.monsterId != null && String(drop.monsterId) === monsterId) return true;
        return monsterName && String(drop.monsterName || '').trim() === monsterName;
      });
      return (matched.length ? matched : this.selectedDrops).slice(0, 8);
    },
    getDropRateText(drop) {
      const raw = drop && (drop.dropRate != null ? drop.dropRate : (drop.rate != null ? drop.rate : drop.probability));
      if (raw == null || raw === '') return '';
      const value = Number(raw);
      if (!Number.isFinite(value)) return String(raw);
      return `${value <= 1 ? (value * 100).toFixed(value * 100 % 1 ? 1 : 0) : value}%`;
    },
    getMapDrops(map) {
      if (!map) return null;
      const mapId = map.mapId == null ? '' : String(map.mapId);
      const mapName = String(map.mapName || '').replace(/\s+/g, '').trim();
      return (this.mapLootsList || []).find(item => {
        if (!item) return false;
        if (mapId && item.mapId != null && String(item.mapId) === mapId) return true;
        return mapName && String(item.mapName || item.name || '').replace(/\s+/g, '').trim() === mapName;
      }) || null;
    },
    getSortedDropItems(items) {
      return (items || []).filter(Boolean).slice().sort((a, b) => (Number(b.color) || 0) - (Number(a.color) || 0));
    },
    getDropColor(color) {
      const colors = { 1: '#8c94a4', 2: '#719324', 3: '#078599', 4: '#1e6fd9', 5: '#7a5cd6', 6: '#b62f59', 7: '#c98a00' };
      return colors[color] || '#657180';
    },
    getDropQualityText(color) {
      const qualities = { 1: '破损', 2: '普通', 3: '良品', 4: '上品', 5: '绝品', 6: '灵宝', 7: '秘宝' };
      return qualities[color] || '';
    },
    getDropDisplayName(name) {
      return String(name || '').replace(/^\s*[\[【](破损|普通|良品|上品|绝品|灵宝|秘宝)[\]】]\s*/, '').trim();
    },
    viewMapDrops(map) {
      this.mapSelectorVisible = false;
      this.$emit('view-drops', map);
    },
    viewMapMonsters(map) {
      this.mapSelectorVisible = false;
      this.$emit('view-monsters', map);
    },
    async loadDungeonMaps(forceRefresh = false) {
      if (!forceRefresh && this.dungeonMaps.length) return;
      this.dungeonLoading = true;
      try {
        const res = await this.$http.get('/gameAmry/queryMapList');
        this.dungeonMaps = ((res.data && res.data.data) || []).map(map => ({ ...map, isDungeonMap: true }));
      } catch (error) {
        this.dungeonMaps = [];
        this.$Message.error('副本地图加载失败');
      } finally {
        this.dungeonLoading = false;
      }
    },
    async refreshSubMaps() {
      await this.loadMonsterCodexList(true);
      if (this.currentRegionKey === 'dungeon') {
        await this.loadDungeonMaps(true);
        this.$Message.success('副本地图已刷新');
        return;
      }
      if (this.$parent && typeof this.$parent.MapList === 'function') this.$parent.MapList();
      this.$Message.success(this.currentRegionKey === 'activity' ? '活动地图已刷新' : '地图数据已刷新');
    },
    async startBattle() {
      if (!this.selectedMap) {
        this.$Message.warning('请先选择一张地图');
        return;
      }
      this.battleLoading = true;
      try {
        this.mapSelectorVisible = false;
        this.$emit('start-battle', this.selectedMap);
      } finally {
        this.battleLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.map-selector-modal >>> .ivu-modal-content {
  overflow: hidden;
  border-radius: 8px;
  background: #f5f7f9;
}

.map-selector-modal >>> .ivu-modal-header,
.map-selector-modal >>> .ivu-modal-close {
  display: none;
}

.map-selector-modal >>> .ivu-modal-body {
  padding: 0;
}

.realm-shell {
  --border: #dfe4ea;
  --muted: #7b8794;
  --text: #25313d;
  display: flex;
  flex-direction: column;
  height: min(760px, 86vh);
  overflow: hidden;
  color: var(--text);
  background: #f5f7f9;
}

.realm-close {
  width: 34px;
  height: 34px;
  margin-left: auto;
  padding: 0;
  font-size: 24px;
}

.realm-toolbar {
  position: relative;
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 8px 22px;
  border-bottom: 1px solid var(--border);
  background: #fbfcfd;
}

.realm-crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 12px;
}

.realm-crumb button {
  padding: 0;
  border: 0;
  color: #1e6fd9;
  background: transparent;
  cursor: pointer;
}

.realm-crumb button.active {
  color: var(--text);
  cursor: default;
}

.realm-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 22px;
}

.overview-heading {
  margin-bottom: 14px;
}

.overview-heading h2 {
  margin: 0 0 5px;
  color: #1e6fd9;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 4px;
}

.overview-heading p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

.overview-path {
  margin-bottom: 11px;
  color: #c98a00;
  font-size: 12px;
}

.region-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.region-card {
  --region-color: #1e6fd9;
  position: relative;
  display: flex;
  min-height: 136px;
  flex-direction: column;
  gap: 5px;
  overflow: hidden;
  padding: 14px 16px 11px 20px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(31, 45, 61, .05);
  cursor: pointer;
  transition: transform .2s, border-color .2s, box-shadow .2s;
}

.region-card::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: var(--region-color);
  content: '';
}

.region-card:hover,
.region-card.current {
  border-color: var(--region-color);
  box-shadow: 0 5px 16px rgba(31, 45, 61, .11);
  transform: translateY(-2px);
}

.region-card.empty {
  opacity: .72;
}

.region-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-icon {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--region-color);
  border-radius: 8px;
  color: var(--region-color);
  background: #fff;
  font-size: 19px;
}

.region-card h3 {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--region-color);
  font-size: 16px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-level {
  flex: 0 0 auto;
  padding: 1px 7px;
  border: 1px solid #ccdaea;
  border-radius: 3px;
  color: #5f7f9f;
  background: #f3f8fd;
  font-size: 11px;
}

.region-type {
  color: #728499;
  font-size: 12px;
}

.region-card-description {
  width: 100%;
  margin: 1px 0 3px;
  overflow: hidden;
  color: #7b8ea3;
  font-size: 12px;
  font-style: italic;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px dashed #dce5ee;
  color: #75889b;
  font-size: 12px;
}

.region-loading {
  position: absolute;
  right: 10px;
  bottom: 10px;
}

.region-map-count {
  flex: 0 0 auto;
}

.region-footer .region-heat {
  margin-right: auto;
  font-family: monospace;
  font-size: 13px;
  letter-spacing: 1px;
}

.region-online {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.region-online i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2e9e5b;
}

.region-online b {
  color: #6f8397;
  font-weight: 400;
}

.heat-low { color: #2e9e5b; }
.heat-mid { color: #c98a00; }
.heat-high { color: #d9534f; }

.region-here {
  position: absolute;
  top: 9px;
  right: 9px;
  padding: 1px 6px;
  border: 1px solid var(--region-color);
  border-radius: 3px;
  color: var(--region-color);
  background: #fff;
  font-size: 12px;
}

.region-arrow {
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: #aab3bd;
}

.region-detail {
  --region-color: #1e6fd9;
  min-height: calc(100% + 36px);
  margin: -18px -22px;
  padding: 18px 22px;
  background: #fff;
}

.region-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 0 2px 14px;
  border: 0;
  border-bottom: 1px solid #e6ebf1;
  border-radius: 0;
  background: #fff;
}

.summary-copy {
  min-width: 0;
  flex: 1;
}

.summary-title-line {
  display: flex;
  align-items: center;
  gap: 9px;
}

.summary-title-line h3 {
  margin: 0;
  font-size: 17px;
}

.summary-title-line span {
  padding: 1px 6px;
  border: 1px solid var(--region-color);
  border-radius: 3px;
  color: var(--region-color);
  font-size: 12px;
}

.summary-copy p {
  margin: 7px 0 0;
  color: var(--muted);
  font-size: 13px;
  font-style: italic;
  line-height: 1.7;
}

.map-detail-layout {
  display: block;
}

.spot-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.spot-card {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 96px;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text);
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: transform .2s, border-color .2s, box-shadow .2s;
}

.spot-card:hover,
.spot-card.selected {
  border-color: var(--region-color);
  box-shadow: 0 4px 13px rgba(31, 45, 61, .09);
  transform: translateY(-1px);
}

.spot-card.selected {
  background: #fbfdff;
}

.spot-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 12px;
}

.spot-name {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  font-size: 15px;
  font-weight: 500;
}

.spot-name > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spot-name > i {
  flex: 0 0 auto;
  padding: 1px 6px;
  border: 1px solid #cbd9e8;
  border-radius: 3px;
  color: #66809a;
  background: #f8fbff;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
}

.spot-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: var(--muted);
  font-size: 12px;
}

.spot-description {
  display: block;
  width: 100%;
  overflow: hidden;
  color: #7b8794;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 5px;
  border-radius: 50%;
  background: #d9534f;
}

.map-intel {
  margin-top: 18px;
  padding: 16px 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(30, 111, 217, .06);
}

.intel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px dashed var(--border);
}

.intel-heading span {
  color: var(--muted);
  font-size: 12px;
}

.intel-heading h3 {
  margin: 0 0 2px;
  color: var(--region-color);
  font-size: 16px;
  letter-spacing: 1px;
}

.monster-loading {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--muted);
  font-size: 12px;
}

.monster-detail-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding-top: 14px;
}

.monster-detail-card {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #d7e3f0;
  border-radius: 8px;
  background: #f4f8fd;
}

.monster-card-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 9px;
}

.monster-card-head strong {
  color: #2f4d6a;
  font-size: 14px;
}

.monster-card-head span,
.monster-card-head em {
  padding: 1px 6px;
  border: 1px solid #c9d9e9;
  border-radius: 3px;
  color: #66809a;
  background: #fff;
  font-size: 11px;
  font-style: normal;
}

.monster-card-head em {
  border-color: #e8c77b;
  color: #a86e00;
  background: #fffaf0;
}

.monster-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 0;
  color: #73869a;
  font-size: 12px;
}

.monster-stats b {
  color: #315a7c;
  font-weight: 500;
}

.monster-loot-title {
  margin-bottom: 6px;
  color: #6f8194;
  font-size: 13px;
  font-weight: 500;
}

.unified-loot {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--border);
}

.monster-empty {
  margin-top: 14px;
}

.intel-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 11px 0;
}

.intel-meta span {
  padding: 7px;
  border: 1px solid var(--border);
  border-radius: 5px;
  color: var(--muted);
  background: #f8fafc;
  font-size: 12px;
}

.intel-meta b {
  display: block;
  margin-top: 2px;
  color: var(--text);
  font-size: 12px;
}

.intel-description {
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px dashed var(--border);
  color: #657180;
  font-size: 12px;
  line-height: 1.75;
}

.intel-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.intel-section-title button {
  padding: 0;
  border: 0;
  color: #1e6fd9;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
}

.loot-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.loot-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  color: #485461;
  background: #fbfcfd;
  font-size: 12px;
}

.loot-item i {
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  border-radius: 50%;
  background: var(--loot-color);
}

.loot-item small {
  color: var(--loot-color);
  font-size: 11px;
}

.loot-empty {
  padding: 14px;
  border: 1px dashed var(--border);
  color: var(--muted);
  font-size: 12px;
  text-align: center;
}

.intel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.realm-loading,
.realm-empty {
  display: flex;
  min-height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--muted);
}

.realm-empty > i {
  color: #9aa6b2;
  font-size: 38px;
}

.realm-empty strong {
  color: var(--text);
}

.realm-empty.compact {
  min-height: 150px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  background: #fff;
}

.realm-footer {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  padding: 7px 22px;
  border-top: 1px solid var(--border);
  color: var(--muted);
  background: #fff;
  font-size: 12px;
}

@media (max-width: 760px) {
  .map-selector-modal >>> .ivu-modal {
    width: calc(100vw - 20px) !important;
    margin: 10px auto;
  }

  .map-selector-modal >>> .ivu-modal-wrap {
    overflow: hidden;
  }

  .realm-shell {
    height: calc(100vh - 20px);
    max-height: none;
  }

  .realm-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 8px 14px;
  }

  .realm-toolbar >>> .ivu-radio-group {
    display: flex;
    width: 100%;
  }

  .realm-close {
    position: absolute;
    top: 4px;
    right: 8px;
    margin-left: 0;
  }

  .realm-toolbar >>> .ivu-radio-wrapper {
    flex: 1;
    padding: 0 5px;
    text-align: center;
  }

  .realm-content {
    padding: 12px;
  }

  .region-grid,
  .spot-list,
  .map-detail-layout {
    grid-template-columns: 1fr;
  }

  .region-card {
    min-height: 170px;
  }

  .region-summary {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .map-intel {
    position: static;
  }

  .intel-meta {
    grid-template-columns: 1fr;
  }

  .intel-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .monster-detail-list {
    grid-template-columns: 1fr;
  }

  .region-detail {
    margin: -12px;
    padding: 12px;
  }

  .realm-footer {
    padding: 7px 14px;
  }
}
</style>

<style>
.map-selector-modal {
  overflow: hidden !important;
}

.map-selector-modal .ivu-modal {
  margin-bottom: 0;
}

.map-selector-modal .ivu-modal-content,
.map-selector-modal .ivu-modal-body {
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/MapSelector.vue